package com.iptvplayer.shared.data.local

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * App-wide user preferences, readable synchronously from anywhere (players,
 * view models, UI) without DI plumbing.
 *
 * Persistence is optional: [ContentCache][com.iptvplayer.shared.data.local.cache.ContentCache]
 * binds a key-value backend (its CacheMeta table) at startup on platforms with
 * SQLite; on web the flows are session-only, which matches the rest of the
 * web app's RAM-mode behavior.
 */
object AppPreferences {

    private const val KEY_AUTOPLAY_NEXT = "pref_autoplay_next"
    private const val KEY_RESUME_PLAYBACK = "pref_resume_playback"
    private const val KEY_PREFER_VLC = "pref_prefer_vlc"

    private var write: ((key: String, value: String) -> Unit)? = null

    private val _autoPlayNextEpisode = MutableStateFlow(true)
    /** Auto-start the next episode when one finishes (series only). */
    val autoPlayNextEpisode: StateFlow<Boolean> = _autoPlayNextEpisode.asStateFlow()

    private val _resumePlayback = MutableStateFlow(true)
    /** Resume VOD from the last watched position instead of starting over. */
    val resumePlayback: StateFlow<Boolean> = _resumePlayback.asStateFlow()

    private val _preferVlcEngine = MutableStateFlow(false)
    /** Start playback in the VLC engine instead of waiting for an ExoPlayer failure (Android). */
    val preferVlcEngine: StateFlow<Boolean> = _preferVlcEngine.asStateFlow()

    /** Called once at startup by the platform's key-value store. */
    fun bind(read: (key: String) -> String?, write: (key: String, value: String) -> Unit) {
        this.write = write
        read(KEY_AUTOPLAY_NEXT)?.let { _autoPlayNextEpisode.value = it == "1" }
        read(KEY_RESUME_PLAYBACK)?.let { _resumePlayback.value = it == "1" }
        read(KEY_PREFER_VLC)?.let { _preferVlcEngine.value = it == "1" }
    }

    fun setAutoPlayNextEpisode(enabled: Boolean) {
        _autoPlayNextEpisode.value = enabled
        write?.invoke(KEY_AUTOPLAY_NEXT, if (enabled) "1" else "0")
    }

    fun setResumePlayback(enabled: Boolean) {
        _resumePlayback.value = enabled
        write?.invoke(KEY_RESUME_PLAYBACK, if (enabled) "1" else "0")
    }

    fun setPreferVlcEngine(enabled: Boolean) {
        _preferVlcEngine.value = enabled
        write?.invoke(KEY_PREFER_VLC, if (enabled) "1" else "0")
    }
}
